import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AppApiGraphiQL } from 'pages/GraphiQL';
import { AppStoreHome } from './pages/AppStore/Home/Home';
import { AppDetails } from './pages/AppStore/AppDetails/AppDetails';
import { PageNotFound } from './pages/PageNotFound/PageNotFound';
import { App } from 'pages/App/App';
import { DeveloperPages } from './pages/Developer/DeveloperPages';
import { DeveloperLogin } from './pages/Developer/Login/DeveloperLogin';
import { DeveloperSignup } from './pages/Developer/Signup/DeveloperSignup';
import { DeveloperHome } from './pages/Developer/Home';
import { ExtranetPages } from './pages/Extranet/ExtranetPages';
import { UserProfileForm } from './pages/Extranet/UserProfileForm/UserProfileForm';
import { UserEditForm } from './pages/Extranet/UserEditForm/UserEditForm';
import { UserList } from './pages/Extranet/UserList/UserList';
import { PayoutList } from './pages/Extranet/PayoutList/PayoutList';
import { AppCreateForm } from './pages/Extranet/AppCreateForm/AppCreateForm';
import { AppDashboard } from './pages/Extranet/AppDashboard/AppDashboard';
import { AppInstallationList } from './pages/Extranet/AppInstallationList/AppInstallationList';
import { AppInstallationDetails } from './pages/Extranet/AppInstallationDetails/AppInstallationDetails';
import { AppEditForm } from './pages/Extranet/AppEditForm/AppEditForm';
import { PricePlanCreateForm } from './pages/Extranet/PricePlanCreateForm/PricePlanCreateForm';
import { PricePlanEditForm } from './pages/Extranet/PricePlanEditForm/PricePlanEditForm';
import { WebhookCreateForm } from './pages/Extranet/WebhookCreateForm/WebhookCreateForm';
import { WebhookEditForm } from './pages/Extranet/WebhookEditForm/WebhookEditForm';
import { ListingCreateForm } from './pages/Extranet/ListingCreateForm/ListingCreateForm';
import { ListingEditForm } from './pages/Extranet/ListingEditForm/ListingEditForm';
import { AppList } from './pages/Extranet/AppList/AppList';
import { OrganizationEditForm } from './pages/Extranet/OrganizationForm/OrganizationEditForm';
import { ExtranetHome } from './pages/Extranet/Home/ExtranetHome';
import { AppStore } from './pages/AppStore/AppStore';
import { AppListings } from './pages/AppStore/AppListings/AppListings';
import { ValidateEmail } from './pages/Developer/ValidateEmail/ValidateEmail';
import { UserCreateForm } from './pages/Extranet/UserCreateForm/UserCreateForm';
import { TaxForm } from './pages/Extranet/TaxForm/TaxForm';
import { PayoutSettings } from './pages/Extranet/PayoutSettings/PayoutSettings';
import { PayoutRequestCreate } from './pages/Extranet/PayoutRequest/PayoutRequestCreate';
import { PayoutRequestEdit } from './pages/Extranet/PayoutRequest/PayoutRequestEdit';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { PrivateRoute } from 'components/PrivateRoute';

export const MainRoutes = (): JSX.Element => {
    const location = useLocation();

    return (
        <App>
            <Routes>
                <Route
                    path="/developer/*"
                    element={
                        <ErrorBoundary key={location.pathname}>
                            <DeveloperPages>
                                <Routes>
                                    <Route path="login" element={<DeveloperLogin />} />
                                    <Route path="signup" element={<DeveloperSignup />} />
                                    <Route path="validate-email" element={<ValidateEmail />} />
                                    <Route path="" element={<DeveloperHome />} />
                                </Routes>
                            </DeveloperPages>
                        </ErrorBoundary>
                    }
                />

                <Route
                    path="/extranet/*"
                    element={
                        <PrivateRoute>
                            <ErrorBoundary key={location.pathname}>
                                <ExtranetPages>
                                    <Routes>
                                        <Route path="userprofile" element={<UserProfileForm />} />
                                        <Route path="team/add" element={<UserCreateForm />} />
                                        <Route path="team/:id" element={<UserEditForm />} />
                                        <Route path="team" element={<UserList />} />
                                        <Route path="apps/new" element={<AppCreateForm />} />
                                        <Route
                                            path="apps/:id/dashboard"
                                            element={<AppDashboard />}
                                        />
                                        <Route
                                            path="apps/:id/installations"
                                            element={<AppInstallationList />}
                                        />
                                        <Route
                                            path="apps/:id/installations/:iid"
                                            element={<AppInstallationDetails />}
                                        />
                                        <Route path="apps/:id/edit" element={<AppEditForm />} />
                                        <Route
                                            path="apps/:id/priceplans/new"
                                            element={<PricePlanCreateForm />}
                                        />
                                        <Route
                                            path="apps/:id/priceplans/:pid/edit"
                                            element={<PricePlanEditForm />}
                                        />
                                        <Route
                                            path="apps/:id/webhooks/new"
                                            element={<WebhookCreateForm />}
                                        />
                                        <Route
                                            path="apps/:id/webhooks/:wid/edit"
                                            element={<WebhookEditForm />}
                                        />
                                        <Route
                                            path="apps/:id/listings/new"
                                            element={<ListingCreateForm />}
                                        />
                                        <Route
                                            path="apps/:id/listings/:lid/edit"
                                            element={<ListingEditForm />}
                                        />
                                        <Route path="apps" element={<AppList />} />
                                        <Route path="account" element={<OrganizationEditForm />} />
                                        <Route
                                            path="payouts/request/create"
                                            element={<PayoutRequestCreate />}
                                        />
                                        <Route
                                            path="payouts/request/:id"
                                            element={<PayoutRequestEdit />}
                                        />
                                        <Route path="payouts" element={<PayoutList />} />
                                        <Route
                                            path="payouts/settings"
                                            element={<PayoutSettings />}
                                        />
                                        <Route path="payouts/tax-form" element={<TaxForm />} />
                                        <Route path="" element={<ExtranetHome />} />
                                    </Routes>
                                </ExtranetPages>
                            </ErrorBoundary>
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/api/*"
                    element={
                        <PrivateRoute>
                            <Routes>
                                <Route path="graphiql/:installId" element={<AppApiGraphiQL />} />
                            </Routes>
                        </PrivateRoute>
                    }
                />

                <Route path="/reference/graphiql/" element={<AppApiGraphiQL />} />

                <Route
                    path="/*"
                    element={
                        <ErrorBoundary key={location.pathname}>
                            <AppStore>
                                <Routes>
                                    <Route path="" element={<AppStoreHome />} />
                                    <Route path="apps" element={<AppListings />} />
                                    <Route path="app-details/:listingId" element={<AppDetails />} />
                                    <Route path="*" element={<PageNotFound />} />
                                </Routes>
                            </AppStore>
                        </ErrorBoundary>
                    }
                />
            </Routes>
        </App>
    );
};
